import { useSigma } from "@react-sigma/core";
import { keyBy, mapValues, sortBy, values } from "lodash";
import { FC, useEffect, useMemo, useState } from "react";
import { AiOutlineCheckCircle, AiOutlineCloseCircle } from "react-icons/ai";
import {
  MdGroupWork,
  MdKeyboardArrowDown,
  MdKeyboardArrowRight,
} from "react-icons/md";

import { Cluster, FiltersState } from "../types";
import Panel from "./Panel";

const ClustersPanel: FC<{
  clusters: Cluster[];
  filters: FiltersState;
  toggleCluster: (cluster: string) => void;
  setClusters: (clusters: Record<string, boolean>) => void;
}> = ({ clusters, filters, toggleCluster, setClusters }) => {
  const sigma = useSigma();
  const graph = sigma.getGraph();
  const [expandedCategories, setExpandedCategories] = useState<
    Record<string, boolean>
  >({
    rca: true,
    sow: true,
    strategy: true,
    other: true,
  });

  const nodesPerCluster = useMemo(() => {
    const index: Record<string, number> = {};
    graph.forEachNode(
      (_, { cluster }) => (index[cluster] = (index[cluster] || 0) + 1)
    );
    return index;
  }, []);

  const maxNodesPerCluster = useMemo(
    () => Math.max(...values(nodesPerCluster)),
    [nodesPerCluster]
  );
  const visibleClustersCount = useMemo(
    () => Object.keys(filters.clusters).length,
    [filters]
  );

  const [visibleNodesPerCluster, setVisibleNodesPerCluster] =
    useState<Record<string, number>>(nodesPerCluster);
  useEffect(() => {
    // To ensure the graphology instance has up to data "hidden" values for
    // nodes, we wait for next frame before reindexing. This won't matter in the
    // UX, because of the visible nodes bar width transition.
    requestAnimationFrame(() => {
      const index: Record<string, number> = {};
      graph.forEachNode(
        (_, { cluster, hidden }) =>
          !hidden && (index[cluster] = (index[cluster] || 0) + 1)
      );
      setVisibleNodesPerCluster(index);
    });
  }, [filters]);

  const sortedClusters = useMemo(
    () => sortBy(clusters, (cluster) => -nodesPerCluster[cluster.key]),
    [clusters, nodesPerCluster]
  );

  // Group clusters by document type
  const groupedClusters = useMemo(() => {
    const groups = {
      rca: [] as Cluster[],
      sow: [] as Cluster[],
      strategy: [] as Cluster[],
      other: [] as Cluster[],
    };

    sortedClusters.forEach((cluster) => {
      const label = cluster.clusterLabel.toLowerCase();
      if (label.includes("rca")) {
        groups.rca.push(cluster);
      } else if (label.includes("sow")) {
        groups.sow.push(cluster);
      } else if (label.includes("strategy")) {
        groups.strategy.push(cluster);
      } else {
        groups.other.push(cluster);
      }
    });

    return groups;
  }, [sortedClusters]);

  const toggleCategory = (category: string) => {
    setExpandedCategories((prev) => ({
      ...prev,
      [category]: !prev[category],
    }));
  };

  const toggleCategoryVisibility = (clusters: Cluster[]) => {
    const allVisible = clusters.every(
      (cluster) => filters.clusters[cluster.key]
    );
    const newClusters = { ...filters.clusters };

    clusters.forEach((cluster) => {
      if (allVisible) {
        delete newClusters[cluster.key];
      } else {
        newClusters[cluster.key] = true;
      }
    });

    setClusters(newClusters);
  };

  const renderClusterList = (
    clusters: Cluster[],
    title: string,
    category: string
  ) => {
    if (clusters.length === 0) return null;

    const isExpanded = expandedCategories[category];
    const totalDocuments = clusters.length;
    const visibleDocuments = clusters.filter(
      (cluster) => filters.clusters[cluster.key]
    ).length;
    const allVisible = clusters.every(
      (cluster) => filters.clusters[cluster.key]
    );
    const allHidden = clusters.every(
      (cluster) => !filters.clusters[cluster.key]
    );

    return (
      <div className="cluster-group">
        <div className="cluster-group-header">
          <button
            className="category-toggle"
            onClick={(e) => {
              e.stopPropagation();
              toggleCategoryVisibility(clusters);
            }}
            title={`${allVisible ? "Hide" : "Show"} all ${title}`}
          >
            <span
              className="circle"
              style={{
                background: allHidden ? "#ccc" : "#4CAF50",
                borderColor: allHidden ? "#ccc" : "#4CAF50",
              }}
            />
          </button>
          <div
            className="cluster-group-header-content"
            onClick={() => toggleCategory(category)}
            title={`${visibleDocuments} of ${totalDocuments} documents visible`}
          >
            {isExpanded ? <MdKeyboardArrowDown /> : <MdKeyboardArrowRight />}
            <h4 className="cluster-group-title">{title}</h4>
            <span className="cluster-group-count">
              {visibleDocuments}/{totalDocuments}
            </span>
          </div>
        </div>
        <div
          className={`cluster-group-content ${isExpanded ? "expanded" : ""}`}
        >
          <ul>
            {clusters.map((cluster) => {
              const nodesCount = nodesPerCluster[cluster.key];
              const visibleNodesCount =
                visibleNodesPerCluster[cluster.key] || 0;
              return (
                <li
                  className="caption-row"
                  key={cluster.key}
                  title={`${nodesCount} node${nodesCount > 1 ? "s" : ""}${
                    visibleNodesCount !== nodesCount
                      ? visibleNodesCount > 0
                        ? ` (only ${
                            visibleNodesCount > 1
                              ? `${visibleNodesCount} are`
                              : "one is"
                          } visible)`
                        : " (all hidden)"
                      : ""
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={filters.clusters[cluster.key] || false}
                    onChange={() => toggleCluster(cluster.key)}
                    id={`cluster-${cluster.key}`}
                  />
                  <label htmlFor={`cluster-${cluster.key}`}>
                    <span
                      className="circle"
                      style={{
                        background: cluster.color,
                        borderColor: cluster.color,
                      }}
                    />{" "}
                    <div className="node-label">
                      <span>{cluster.clusterLabel}</span>
                      <div
                        className="bar"
                        style={{
                          width: (100 * nodesCount) / maxNodesPerCluster + "%",
                        }}
                      >
                        <div
                          className="inside-bar"
                          style={{
                            width: (100 * visibleNodesCount) / nodesCount + "%",
                          }}
                        />
                      </div>
                    </div>
                  </label>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    );
  };

  return (
    <Panel
      title={
        <>
          <MdGroupWork className="text-muted" /> Artifact Clusters
          {visibleClustersCount < clusters.length ? (
            <span className="text-muted text-small">
              {" "}
              ({visibleClustersCount} / {clusters.length})
            </span>
          ) : (
            ""
          )}
        </>
      }
    >
      <p>
        <i className="text-muted">
          Click an artifact cluster to show/hide all items derived from that
          document.
        </i>
      </p>
      <p className="buttons">
        <button
          className="btn"
          onClick={() =>
            setClusters(mapValues(keyBy(clusters, "key"), () => true))
          }
        >
          <AiOutlineCheckCircle /> Check all
        </button>{" "}
        <button className="btn" onClick={() => setClusters({})}>
          <AiOutlineCloseCircle /> Uncheck all
        </button>
      </p>
      {renderClusterList(groupedClusters.rca, "RCA Documents", "rca")}
      {renderClusterList(groupedClusters.sow, "SOWs", "sow")}
      {renderClusterList(
        groupedClusters.strategy,
        "National Strategy Documents",
        "strategy"
      )}
      {renderClusterList(groupedClusters.other, "Other Documents", "other")}
    </Panel>
  );
};

export default ClustersPanel;
